import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import ChartTooltip from '../components/ChartTooltip'
import CollapsibleCard from '../components/CollapsibleCard'
import KwhCell from '../components/KwhCell'
import MoneyCell from '../components/MoneyCell'
import PercentBarCell from '../components/PercentBarCell'
import RateCell from '../components/RateCell'
import SaberTable from '../components/SaberTable'
import type { SaberTableColumn } from '../components/SaberTable'
import StatusBadge from '../components/StatusBadge'
import {
  useGetPortfolioCompletenessQuery,
  useGetPropertyCompletenessQuery,
  useGetPropertyTenantsQuery,
  useGetPropertyTouBreakdownQuery,
  useGetShadowBillingQuery,
  useGetVarianceQuery
} from '../saberApi'
import type { TouBreakdownRow } from '../saber.types'
import { ALL_PROPERTIES, currentMonthStart, useSaberSelection } from '../SaberContext'
import { getSaberChartTheme, darkenHex } from '../theme'
import { date, integer, kwh, money, money0, percent, signedMoney } from '../format'
import { CELL_COLOR } from '../cellColor'
import NullDash from '../components/NullDash'
import { completenessPctRaw, completenessStatusFromPct } from './detail/completeness'
import CompletenessTable from './detail/CompletenessTable'
import GroupedVarianceTable from './detail/GroupedVarianceTable'
import type { VarianceSelection } from './detail/GroupedVarianceTable'
import {
  categoryLabel,
  categoryTooltip,
  completenessStatusTooltip,
  resolvePropertyDisplay,
  touBreakdownSectionTooltip,
  VARIANCE_CATEGORY_ORDER,
  varianceColorClass
} from './detail/labels'
import SaberControlBar, { type SaberView, SABER_TEAL_ACCENT } from '../components/SaberControlBar'
import { CalendarIcon } from '@heroicons/react/24/outline'
import { FilterLabeledControl, MultiSegmentedControl, SegmentedControl } from '../components/Controls'
import PropertyPicker from './detail/PropertyPicker'
// WEB-6581: Shadow Billing's period picker shares the Dashboard's model so the
// two period filters are identical (2 quarters + 4 months + Custom).
import { buildDashboardPeriodOptions, defaultDashboardPeriodKey, resolveDashboardPeriod } from './dashboard/dashboardPeriod'
import { deriveInvoiceMarkers } from './detail/markers'
import ProductionChart from './detail/ProductionChart'
import TenantAllocTable from './detail/TenantAllocTable'
import VarianceDrawer from './detail/VarianceDrawer'
import { useStickyPanelOffset } from '../useStickyPanelOffset'

// v001 Tab 2 — Detail. Per-property billing deep-dive: the old ShadowBilling +
// VarianceExplorer unified into one scrollable five-section view (§6). A
// TypeAheadSelect property picker plus a §4 month-range picker resolve a
// single {from,to} window that drives all five sections.

// Emphasized value inside a collapsed-summary line. A toning className (green/
// amber/red) replaces the default slate so the classes never conflict.
const Strong: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <span className={classnames('font-medium', className ?? 'text-slate-700 dark:text-slate-200')}>{children}</span>
)

// Error block with retry, matching the old screens' convention.
const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="flex flex-col items-start gap-2 py-6 text-sm text-red-600 dark:text-red-400">
    <span>{message}</span>
    <button
      type="button"
      onClick={onRetry}
      className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
    >
      Retry
    </button>
  </div>
)

const Loading: React.FC = () => (
  <div className="py-10 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
)

// WEB-6571: KPI tiles above the charts, mirroring the prototype's summary row.
const TILE_ACCENT = {
  indigo: 'text-indigo-600 dark:text-indigo-400',
  emerald: CELL_COLOR.positive,
  sky: 'text-sky-600 dark:text-sky-400',
  amber: CELL_COLOR.warning
} as const
const Tile: React.FC<{ label: string; value: string; sub?: string; accent: keyof typeof TILE_ACCENT }> = ({
  label,
  value,
  sub,
  accent
}) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/40">
    <div className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</div>
    <div className={classnames('mt-1 text-xl font-semibold', TILE_ACCENT[accent])}>{value}</div>
    {sub != null && <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{sub}</div>}
  </div>
)

// Severity order for the §4 completeness status filter chips (best → worst).
const COMPLETENESS_STATUS_ORDER = ['OK', 'WARN', 'CRITICAL']

const DETAIL_SECTIONS = ['production', 'tenant', 'tou', 'completeness', 'variance'] as const
type DetailSection = (typeof DETAIL_SECTIONS)[number]

const INITIAL_SECTION_OPEN: Record<DetailSection, boolean> = {
  production: true,
  tenant: true,
  tou: true,
  completeness: true,
  variance: true
}

const EXPAND_ALL_BUTTON =
  'text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300'

export const Detail: React.FC = () => {
  // §13 — context carries cross-tab navigation: the selected property and an
  // optional month requested by a Dashboard row click. Default to the current
  // month (the most recent month chip), matching the Dashboard's default.
  const { propertyCuid, setPropertyCuid, period: ctxPeriod, setPeriod, customRange, setCustomRange } = useSaberSelection()
  const today = useMemo(() => new Date(), [])
  // WEB-6581: the Period bar's choice set matches the Dashboard — the 2 most
  // recent quarters, the 4 most recent months, and Custom — and the selection
  // itself lives in SaberContext (URL-backed), so it persists across the
  // Dashboard ⇄ Shadow Billing tab switch. Fall back to the current-month
  // default until a period is chosen (a Dashboard row click sets it).
  const periods = useMemo(() => buildDashboardPeriodOptions(today), [today])
  // `period` is a key from buildDashboardPeriodOptions (a 'q-YYYY-N' quarter, a
  // 'YYYY-MM' month, or 'custom'); `customRange` backs Custom and the chart's
  // click-to-scope (WEB-6572); `view` toggles daily/monthly (Shadow-Billing-only,
  // so it stays local).
  const period = ctxPeriod ?? defaultDashboardPeriodKey(today)
  const [view, setView] = useState<SaberView>('daily')
  const [hiddenCategories, setHiddenCategories] = useState<ReadonlySet<string>>(() => new Set())
  const [hiddenStatuses, setHiddenStatuses] = useState<ReadonlySet<string>>(() => new Set())
  const [selectedInvoice, setSelectedInvoice] = useState<VarianceSelection | null>(null)
  const [sectionOpen, setSectionOpen] = useState<Record<DetailSection, boolean>>(() => ({ ...INITIAL_SECTION_OPEN }))
  const [expandAllVersion, setExpandAllVersion] = useState(0)
  const [expandAllOpen, setExpandAllOpen] = useState(true)
  const allSectionsExpanded = DETAIL_SECTIONS.every((key) => sectionOpen[key])
  const onSectionOpenChange = (key: DetailSection) => (open: boolean): void => {
    setSectionOpen((prev) => ({ ...prev, [key]: open }))
  }
  const toggleAllSections = (): void => {
    const next = !allSectionsExpanded
    setExpandAllOpen(next)
    setExpandAllVersion((version) => version + 1)
    setSectionOpen(Object.fromEntries(DETAIL_SECTIONS.map((key) => [key, next])) as Record<DetailSection, boolean>)
  }

  // Measure the sticky page header so section headers can pin just below it.
  const { pageHeaderRef, panelStickyProps } = useStickyPanelOffset()

  // Property options: OPERATING properties from the (current-month) portfolio.
  const { data: portfolio } = useGetPortfolioCompletenessQuery({ month: currentMonthStart() })
  const operating = useMemo(
    () => (portfolio?.properties ?? []).filter((p) => p.status === 'OPERATING'),
    [portfolio]
  )
  const cuid = propertyCuid !== ALL_PROPERTIES ? propertyCuid : operating[0]?.propertyCuid ?? ''
  const selectedProperty =
    operating.find((p) => p.propertyCuid === cuid) ??
    (portfolio?.properties ?? []).find((p) => p.propertyCuid === cuid)
  const propertyOptions = useMemo(
    () =>
      operating.map((p) => {
        const { name, location } = resolvePropertyDisplay(p.propertyName, p.location)
        return {
          value: p.propertyCuid,
          label: name,
          sublabel: location ?? undefined,
          detail: p.utility ?? undefined
        }
      }),
    [operating]
  )

  // Resolved range drives ALL five sections. Quarters/months resolve to their
  // full calendar window; Custom is null until both ends are picked, so the
  // queries skip until the range is complete (matches the Dashboard).
  const resolved = resolveDashboardPeriod(period, customRange)
  const from = resolved?.from ?? ''
  const to = resolved?.to ?? ''
  const rangeReady = resolved != null
  const skip = cuid === '' || !rangeReady
  // Picking a chip switches period; the first hop into Custom seeds the editable
  // range from whatever window is currently resolved (one URL write — see
  // setPeriod's contract in SaberContext).
  const onPeriodChange = (key: string): void => {
    if (key === 'custom' && customRange == null && resolved != null) setPeriod(key, resolved)
    else setPeriod(key)
  }

  const {
    data: shadow,
    isLoading: shadowLoading,
    isError: shadowError,
    refetch: refetchShadow
  } = useGetShadowBillingQuery({ cuid, from, to, granularity: view }, { skip })
  const { data: tenants } = useGetPropertyTenantsQuery({ cuid, from, to }, { skip })
  const { data: touRows } = useGetPropertyTouBreakdownQuery({ cuid, from, to }, { skip })
  const { data: meterRows } = useGetPropertyCompletenessQuery({ cuid, from, to }, { skip })
  const {
    data: variance,
    isLoading: varianceLoading,
    isError: varianceError,
    refetch: refetchVariance
  } = useGetVarianceQuery({ propertyCuid: cuid, from, to }, { skip })

  const theme = getSaberChartTheme()
  const series = shadow?.series ?? []
  const totals = shadow?.totals

  // Inclusive day count of the resolved window (for the per-day summaries).
  const dayCount = useMemo(() => {
    const ms = Date.parse(to) - Date.parse(from)
    return Number.isNaN(ms) ? 1 : Math.max(1, Math.round(ms / 86_400_000) + 1)
  }, [from, to])

  // §6 collapsed-summary aggregates.
  const avgDailyKwh = totals == null ? null : totals.allocatedKwh / dayCount
  const tenantRows = useMemo(() => tenants ?? [], [tenants])
  const tenantTotals = useMemo(() => {
    if (tenantRows.length === 0) return null
    return tenantRows.reduce(
      (acc, r) => ({
        allocationPct: acc.allocationPct + r.allocationPct,
        allocatedKwh: acc.allocatedKwh + r.allocatedKwh,
        grossRevenue: acc.grossRevenue + r.grossRevenue,
        netRevenue: acc.netRevenue + r.netRevenue
      }),
      { allocationPct: 0, allocatedKwh: 0, grossRevenue: 0, netRevenue: 0 }
    )
  }, [tenantRows])
  const touList = useMemo(() => touRows ?? [], [touRows])
  const touTotalKwh = useMemo(
    () => (touList.length === 0 ? 0 : touList.reduce((acc, r) => acc + r.allocatedKwh, 0)),
    [touList]
  )
  const touTotals = useMemo(() => {
    if (touList.length === 0) return null
    return touList.reduce(
      (acc, r) => ({ allocatedKwh: acc.allocatedKwh + r.allocatedKwh, grossRevenue: acc.grossRevenue + r.grossRevenue }),
      { allocatedKwh: 0, grossRevenue: 0 }
    )
  }, [touList])

  const touColumns: Array<SaberTableColumn<TouBreakdownRow>> = useMemo(
    () => [
      { key: 'season', header: 'Season', sortable: true, sortValue: (r) => r.season, render: (r) => <StatusBadge status={r.season} palette="season" /> },
      {
        key: 'tou',
        header: 'TOU',
        sortable: true,
        sortValue: (r) => r.tou,
        render: (r) => <StatusBadge status={r.tou} palette="tou" />
      },
      {
        key: 'hours',
        header: 'Hours',
        sortable: true,
        sortValue: (r) => r.hours ?? '',
        render: (r) =>
          r.hours != null && r.hours !== '' ? (
            <span className={CELL_COLOR.secondary}>{r.hours}</span>
          ) : (
            <NullDash />
          )
      },
      {
        key: 'tariff',
        header: 'Tariff',
        sortable: true,
        sortValue: (r) => r.tariffSchedule,
        render: (r) => <StatusBadge status={r.tariffSchedule} palette="tariff" />
      },
      {
        key: 'kwh',
        header: 'Shadow kWh',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.allocatedKwh,
        render: (r) => <KwhCell value={r.allocatedKwh} />
      },
      {
        key: 'pct',
        header: '% of Production',
        align: 'right',
        sortable: true,
        sortValue: (r) => (touTotalKwh > 0 ? (r.allocatedKwh / touTotalKwh) * 100 : null),
        render: (r) => {
          const pct = touTotalKwh > 0 ? (r.allocatedKwh / touTotalKwh) * 100 : null
          return (
            <PercentBarCell value={pct} max={50} ariaDescription="of production" />
          )
        }
      },
      {
        key: 'rate',
        header: 'Eff. Rate',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.effectiveRate,
        render: (r) => <RateCell value={r.effectiveRate} />
      },
      {
        key: 'gross',
        header: 'Gross $',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.grossRevenue,
        render: (r) => <MoneyCell value={r.grossRevenue} role="money" />
      }
    ],
    [touTotalKwh]
  )
  const meterList = useMemo(() => meterRows ?? [], [meterRows])
  const warnCount = meterList.filter((r) => r.status !== 'OK').length
  const unbilledKwhTotal = meterList.length === 0 ? null : meterList.reduce((acc, r) => acc + r.unbilledKwh, 0)
  const completenessHealth = useMemo(() => {
    if (meterList.length === 0) return null
    const allocatedKwh = meterList.reduce((acc, r) => acc + r.allocatedKwh, 0)
    const billedKwh = meterList.reduce((acc, r) => acc + r.billedKwh, 0)
    return completenessStatusFromPct(completenessPctRaw({ allocatedKwh, billedKwh }))
  }, [meterList])
  const presentStatuses = useMemo(() => new Set<string>(meterList.map((r) => r.status)), [meterList])
  const toggleStatus = (s: string): void =>
    setHiddenStatuses((prev) => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s)
      else next.add(s)
      return next
    })
  const filteredMeters = useMemo(
    () => meterList.filter((r) => !hiddenStatuses.has(r.status)),
    [meterList, hiddenStatuses]
  )
  const varianceRows = useMemo(() => variance ?? [], [variance])
  const presentCategories = useMemo(() => new Set(varianceRows.map((r) => r.varianceCategory)), [varianceRows])
  const toggleCategory = (c: string): void =>
    setHiddenCategories((prev) => {
      const next = new Set(prev)
      if (next.has(c)) next.delete(c)
      else next.add(c)
      return next
    })
  const filteredVariance = useMemo(
    () => varianceRows.filter((r) => !hiddenCategories.has(r.varianceCategory)),
    [varianceRows, hiddenCategories]
  )
  const netVariance = varianceRows.length === 0 ? null : varianceRows.reduce((acc, r) => acc + r.totalVariance, 0)
  const materialCount = varianceRows.filter((r) => r.isMaterial).length

  // §7 — invoice boundary markers for the production chart.
  const markers = useMemo(() => deriveInvoiceMarkers(varianceRows), [varianceRows])

  return (
    // WEB-6581: drop the screen-level top padding (the layout already pads) to
    // reclaim vertical space for the data; pb-4 keeps bottom breathing room.
    <div className="space-y-4 pb-4">
      {/* WEB-6570/6573: consolidated header — title with subtitle inline; property
          picker · location · utility · resolved date-range on the row below. */}
      <div
        ref={pageHeaderRef}
        className="sticky top-0 z-10 -mx-4 space-y-1 border-b border-slate-200 bg-white px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8"
      >
        <div className="space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Shadow Billing</h2>
            <p className="m-0 text-xs text-slate-500 dark:text-slate-400">
              Independent daily gross solar value calculations
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <PropertyPicker
            label={selectedProperty?.propertyName ?? 'Select a property'}
            options={propertyOptions}
            value={cuid === '' ? null : cuid}
            onSelect={setPropertyCuid}
            suffix={
              selectedProperty?.utility != null ? (
                <>
                  <span aria-hidden className="text-slate-300 dark:text-slate-600">·</span>
                  <span className="pt-0.5 text-base font-semibold text-indigo-600 dark:text-indigo-400">{selectedProperty.utility}</span>
                </>
              ) : undefined
            }
          />
          {selectedProperty?.location != null && (
            <>
              <span aria-hidden className="text-slate-300 dark:text-slate-600">·</span>
              <span className={classnames('text-base font-medium', CELL_COLOR.positive)}>
                {selectedProperty.location}
              </span>
            </>
          )}
          {rangeReady && (
            <>
              <span aria-hidden className="text-slate-300 dark:text-slate-600">·</span>
              <span className={classnames('inline-flex items-center gap-1 text-sm font-medium', SABER_TEAL_ACCENT)}>
                <CalendarIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {date(from)} – {date(to)}
              </span>
            </>
          )}
        </div>

        <SaberControlBar
          periods={periods}
          period={period}
          onPeriodChange={onPeriodChange}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
      </div>

      {/* WEB-6571: prototype KPI tiles, driven by the resolved window's totals. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Production"
          value={kwh(totals?.allocatedKwh)}
          sub={`${kwh(avgDailyKwh)} avg/day`}
          accent="indigo"
        />
        <Tile
          label="TOU Value"
          value={money0(totals?.grossValue)}
          sub={`${money0(totals == null ? null : totals.grossValue / dayCount)} avg/day`}
          accent="emerald"
        />
        <Tile
          label="Period Total (Net)"
          value={money0(totals?.netValue)}
          sub="gross − corp discount"
          accent="sky"
        />
        <Tile
          label="Unbilled Revenue"
          value={money0(totals?.unbilledValue)}
          sub={`${kwh(totals?.unbilledKwh)} unbilled`}
          accent="amber"
        />
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={toggleAllSections} className={EXPAND_ALL_BUTTON}>
          {allSectionsExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {/* 1 — Production and Value */}
      <CollapsibleCard
        title="Production and Value"
        defaultOpen
        expandAllOpen={expandAllOpen}
        expandAllVersion={expandAllVersion}
        onOpenChange={onSectionOpenChange('production')}
        {...panelStickyProps(1)}
        summary={
          <span>
            <Strong>{kwh(avgDailyKwh)}</Strong> avg/day · <Strong>{money0(totals?.grossValue)}</Strong> period gross
          </span>
        }
      >
        {shadowError ? (
          <ErrorBlock
            message="Failed to load shadow billing."
            onRetry={() => {
              refetchShadow().catch(() => undefined)
            }}
          />
        ) : shadowLoading ? (
          <Loading />
        ) : (
          <>
            <div className="mb-2 flex flex-wrap items-center justify-start gap-4">
              <FilterLabeledControl label="View">
                <SegmentedControl
                  ariaLabel="Chart granularity"
                  options={[
                    { value: 'daily', label: 'Daily' },
                    { value: 'monthly', label: 'Monthly' }
                  ]}
                  value={view}
                  onChange={setView}
                />
              </FilterLabeledControl>
            </div>
            <div className="grid grid-cols-1 gap-4 overflow-hidden lg:grid-cols-2">
            <ProductionChart
              title={`${view === 'daily' ? 'Daily' : 'Monthly'} Production`}
              series={series}
              markers={markers}
              onSelectInvoiceRange={(from, to) => setPeriod('custom', { from, to })}
            />
            <div className="overflow-hidden">
              <div className="mb-2 text-center text-xs font-medium text-slate-700 dark:text-slate-300">
                {view === 'daily' ? 'Daily' : 'Monthly'} Value
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis dataKey="period" tick={{ fontSize: 10, fill: theme.axis }} stroke={theme.grid} />
                  <YAxis tick={{ fontSize: 10, fill: theme.axis }} stroke={theme.grid} width={48} />
                  <Tooltip content={<ChartTooltip prefix="$" />} cursor={false} />
                  <Bar
                    dataKey="grossValue"
                    name="Gross value"
                    fill={theme.series[1]}
                    radius={[3, 3, 0, 0]}
                    activeBar={{ fill: darkenHex(theme.series[1], 0.18) }}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-4 rounded-sm" style={{ background: theme.series[1] }} />
                  Gross value ($)
                </span>
              </div>
            </div>
          </div>
          </>
        )}
      </CollapsibleCard>

      {/* 2 — Tenant Allocation & TOU Value */}
      <CollapsibleCard
        title="Tenant Allocation & TOU Value"
        defaultOpen
        expandAllOpen={expandAllOpen}
        expandAllVersion={expandAllVersion}
        onOpenChange={onSectionOpenChange('tenant')}
        {...panelStickyProps(2)}
        summary={
          // WEB-6583(c): show the period totals that match the table's TOTAL row
          // (alloc % · allocated kWh · gross $ · net $), in column order, so the
          // collapsed summary and the expanded footer agree.
          <span>
            <Strong>{percent(tenantTotals?.allocationPct, 0)}</Strong> allocated ·{' '}
            <Strong>{integer(tenantTotals?.allocatedKwh)}</Strong> kWh ·{' '}
            <Strong className={CELL_COLOR.positive}>{money(tenantTotals?.grossRevenue)}</Strong> gross ·{' '}
            <Strong className={CELL_COLOR.positive}>{money(tenantTotals?.netRevenue)}</Strong> net
          </span>
        }
      >
        <TenantAllocTable rows={tenantRows} propertyName={selectedProperty?.propertyName} />
      </CollapsibleCard>

      {/* 3 — TOU Rate Breakdown (normalized) */}
      <CollapsibleCard
        title={
          <span className="cursor-help" title={touBreakdownSectionTooltip}>
            TOU Rate Breakdown (normalized)
          </span>
        }
        defaultOpen
        expandAllOpen={expandAllOpen}
        expandAllVersion={expandAllVersion}
        onOpenChange={onSectionOpenChange('tou')}
        {...panelStickyProps(3)}
        summary={
          <span>
            <Strong>{touList.length}</Strong> periods · <Strong>{integer(touTotals?.allocatedKwh)}</Strong> kWh ·{' '}
            <Strong>{money(touTotals?.grossRevenue)}</Strong>
          </span>
        }
      >
        <SaberTable
          columns={touColumns}
          rows={touList}
          rowKey={(r) => `${r.season}::${r.tou}::${r.tariffSchedule}`}
          emptyMessage="No TOU breakdown."
          helpKey="tou"
        />
      </CollapsibleCard>

      {/* 4 — Completeness Check */}
      <CollapsibleCard
        title="Completeness Check"
        defaultOpen
        expandAllOpen={expandAllOpen}
        expandAllVersion={expandAllVersion}
        onOpenChange={onSectionOpenChange('completeness')}
        {...panelStickyProps(4)}
        summary={
          <span className="inline-flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
            {completenessHealth != null && (
              <>
                <div className="-mr-2">
                  <StatusBadge status={completenessHealth} title={completenessStatusTooltip(completenessHealth)} />
                </div>
                <span aria-hidden>·</span>
              </>
            )}
            <span
              className={classnames(
                'font-medium',
                warnCount === 0 ? CELL_COLOR.positive : CELL_COLOR.warning
              )}
            >
              ⚠ {warnCount} warnings
            </span>
            <span aria-hidden>·</span>
            <span>
              <Strong>{integer(unbilledKwhTotal)}</Strong> unbilled kWh
            </span>
            <span aria-hidden>·</span>
            <Strong className={CELL_COLOR.negative}>{money0(totals?.unbilledValue)}</Strong>
          </span>
        }
      >
        <CompletenessTable
          rows={filteredMeters}
          propertyName={selectedProperty?.propertyName}
          actions={
            <FilterLabeledControl label="Status">
              <MultiSegmentedControl
                ariaLabel="Filter by completeness status"
                options={COMPLETENESS_STATUS_ORDER}
                isAvailable={(s) => presentStatuses.has(s)}
                isActive={(s) => !hiddenStatuses.has(s)}
                onToggle={toggleStatus}
                getTitle={completenessStatusTooltip}
              />
            </FilterLabeledControl>
          }
        />
      </CollapsibleCard>

      {/* 5 — Variance — Invoices */}
      <CollapsibleCard
        title="Variance — Invoices"
        defaultOpen
        expandAllOpen={expandAllOpen}
        expandAllVersion={expandAllVersion}
        onOpenChange={onSectionOpenChange('variance')}
        {...panelStickyProps(5)}
        summary={
          <span>
            <Strong>{varianceRows.length}</Strong> invoices ·{' '}
            <Strong className={varianceColorClass(netVariance)}>
              {signedMoney(netVariance)}
            </Strong>{' '}
            net · <Strong className={CELL_COLOR.negative}>{materialCount}</Strong> material
          </span>
        }
      >
        {varianceError ? (
          <ErrorBlock
            message="Failed to load variance."
            onRetry={() => {
              refetchVariance().catch(() => undefined)
            }}
          />
        ) : varianceLoading ? (
          <Loading />
        ) : (
          <GroupedVarianceTable
            rows={filteredVariance}
            onSelect={setSelectedInvoice}
            propertyName={selectedProperty?.propertyName}
            actions={
              <FilterLabeledControl label="Variance">
                <MultiSegmentedControl
                  ariaLabel="Filter by variance category"
                  options={VARIANCE_CATEGORY_ORDER}
                  isAvailable={(c) => presentCategories.has(c)}
                  isActive={(c) => !hiddenCategories.has(c)}
                  onToggle={toggleCategory}
                  formatLabel={(c) => categoryLabel(c).toUpperCase()}
                  getTitle={categoryTooltip}
                />
              </FilterLabeledControl>
            }
          />
        )}
      </CollapsibleCard>

      {selectedInvoice != null && <VarianceDrawer selection={selectedInvoice} onClose={() => setSelectedInvoice(null)} />}
    </div>
  )
}

export default Detail
